import { useState, useEffect } from 'react';
import { apiFetch } from '../../lib/api';

export interface QAPolicyData {
  requiredTestTypes:    Array<'unit' | 'integration' | 'e2e' | 'performance' | 'accessibility'>;
  coverageThreshold:    number;
  requiresManualCases:  boolean;
  signOffRole:          'QA_ENGINEER' | 'TECH_LEAD' | 'PRODUCT_MANAGER' | 'AUTOMATED_GATE';
  qaEnvironment:        'SHARED_STAGING' | 'DEDICATED_QA' | 'LOCAL_ONLY';
  testDataStrategy:     'FIXTURES' | 'GENERATED' | 'ANONYMISED_PROD' | 'NONE';
  blockDeployOnFailure: boolean;
}

interface Props {
  tenantId:    string;
  /** Seed values (wizard passes prior-step state). When omitted, the form
   *  loads the existing policy on mount, falling back to DEFAULT. */
  savedData?:  QAPolicyData;
  /** Label for the submit button. Wizard: "Save & Continue →"; settings: "Save changes". */
  submitLabel?: string;
  /** Called after a successful POST. Wizard wires this to its step-advance handler. */
  onSaved?:    (data: QAPolicyData) => void;
  /** When false, the form is read-only (no edits, no submit). Settings passes isAdmin. */
  canEdit?:    boolean;
  /** When true (default) renders the "QA Policy" heading + intro. Pages with their
   *  own heading (settings) pass false to avoid a duplicate title. */
  showIntro?:  boolean;
}

// ── Shared styles ─────────────────────────────────────────────────────────────

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: 'var(--font-body)',
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--text-secondary)',
  marginBottom: 6,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const helpStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: 12,
  color: 'var(--text-secondary)',
  marginTop: 6,
  lineHeight: 1.5,
};

const fieldGap: React.CSSProperties = { marginBottom: 24 };

// ── Sub-components ────────────────────────────────────────────────────────────

type TestType = 'unit' | 'integration' | 'e2e' | 'performance' | 'accessibility';

const TEST_TYPE_LABELS: Record<TestType, string> = {
  unit:          'Unit tests',
  integration:   'Integration tests',
  e2e:           'End-to-end (E2E) tests',
  performance:   'Performance tests',
  accessibility: 'Accessibility tests',
};

function CheckboxGroup({
  values,
  onChange,
  disabled,
}: {
  values: TestType[];
  onChange: (v: TestType[]) => void;
  disabled?: boolean;
}) {
  const toggle = (type: TestType) => {
    if (disabled) return;
    onChange(
      values.includes(type)
        ? values.filter(v => v !== type)
        : [...values, type],
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {(Object.keys(TEST_TYPE_LABELS) as TestType[]).map(type => {
        const checked = values.includes(type);
        return (
          <label
            key={type}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '9px 14px',
              borderRadius: 'var(--radius-sm)',
              border: `1px solid ${checked ? 'var(--primary)' : 'var(--border)'}`,
              background: checked ? 'rgba(124,58,237,0.06)' : 'var(--surface-alt)',
              cursor: disabled ? 'default' : 'pointer',
              opacity: disabled && !checked ? 0.7 : 1,
              fontFamily: 'var(--font-body)',
              fontSize: 14,
              fontWeight: checked ? 600 : 400,
              color: checked ? 'var(--primary)' : 'var(--text)',
              transition: 'border-color 150ms, background 150ms',
            }}
          >
            <input
              type="checkbox"
              checked={checked}
              disabled={disabled}
              onChange={() => toggle(type)}
              style={{ accentColor: 'var(--primary)', width: 16, height: 16 }}
            />
            {TEST_TYPE_LABELS[type]}
          </label>
        );
      })}
    </div>
  );
}

function RadioGroup<T extends string>({
  name,
  options,
  value,
  onChange,
  disabled,
}: {
  name: string;
  options: Array<{ value: T; label: string; description: string }>;
  value: T;
  onChange: (v: T) => void;
  disabled?: boolean;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {options.map(opt => {
        const selected = value === opt.value;
        return (
          <label
            key={opt.value}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              padding: '10px 14px',
              borderRadius: 'var(--radius-sm)',
              border: `1px solid ${selected ? 'var(--primary)' : 'var(--border)'}`,
              background: selected ? 'rgba(124,58,237,0.06)' : 'var(--surface-alt)',
              cursor: disabled ? 'default' : 'pointer',
              opacity: disabled && !selected ? 0.7 : 1,
              transition: 'border-color 150ms, background 150ms',
            }}
          >
            <input
              type="radio"
              name={name}
              value={opt.value}
              checked={selected}
              disabled={disabled}
              onChange={() => onChange(opt.value)}
              style={{ accentColor: 'var(--primary)', marginTop: 3 }}
            />
            <div>
              <p style={{
                fontFamily: 'var(--font-body)', fontSize: 14,
                fontWeight: selected ? 600 : 400,
                color: selected ? 'var(--primary)' : 'var(--text)',
                margin: 0,
              }}>
                {opt.label}
              </p>
              <p style={{
                fontFamily: 'var(--font-body)', fontSize: 12,
                color: 'var(--text-secondary)', margin: '2px 0 0',
              }}>
                {opt.description}
              </p>
            </div>
          </label>
        );
      })}
    </div>
  );
}

function Toggle({
  checked,
  onChange,
  label,
  description,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  description?: string;
  disabled?: boolean;
}) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 14px',
        borderRadius: 'var(--radius-sm)',
        border: `1px solid ${checked ? 'var(--primary)' : 'var(--border)'}`,
        background: checked ? 'rgba(124,58,237,0.06)' : 'var(--surface-alt)',
        cursor: disabled ? 'default' : 'pointer',
        transition: 'border-color 150ms, background 150ms',
      }}
    >
      <div
        onClick={() => { if (!disabled) onChange(!checked); }}
        style={{
          width: 40, height: 22, borderRadius: 11,
          background: checked ? 'var(--primary)' : 'var(--border)',
          position: 'relative', flexShrink: 0, cursor: disabled ? 'default' : 'pointer',
          transition: 'background 150ms',
        }}
      >
        <div style={{
          position: 'absolute', top: 3, left: checked ? 21 : 3,
          width: 16, height: 16, borderRadius: '50%', background: '#fff',
          transition: 'left 150ms', boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
        }} />
      </div>
      <div>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 500, color: 'var(--text)', margin: 0 }}>
          {label}
        </p>
        {description && (
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', margin: '2px 0 0' }}>
            {description}
          </p>
        )}
      </div>
    </label>
  );
}

function PassRateBadge({ value }: { value: number }) {
  let color: string;
  let bg: string;
  let label: string;
  if (value < 60)       { color = 'var(--error)';   bg = 'rgba(239,68,68,0.1)';   label = 'Low'; }
  else if (value < 80)  { color = 'var(--warning)'; bg = 'rgba(245,158,11,0.1)'; label = 'Standard'; }
  else                  { color = 'var(--success)'; bg = 'rgba(34,197,94,0.1)';  label = 'High'; }

  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 4,
      fontFamily: 'var(--font-body)',
      fontSize: 11,
      fontWeight: 700,
      color,
      background: bg,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
    }}>
      {label}
    </span>
  );
}

const DEFAULT: QAPolicyData = {
  requiredTestTypes:    ['unit'],
  coverageThreshold:    70,
  requiresManualCases:  false,
  signOffRole:          'QA_ENGINEER',
  qaEnvironment:        'SHARED_STAGING',
  testDataStrategy:     'FIXTURES',
  blockDeployOnFailure: true,
};

export default function QAPolicyForm({
  tenantId,
  savedData,
  submitLabel = 'Save changes',
  onSaved,
  canEdit = true,
  showIntro = true,
}: Props) {
  const [testTypes,         setTestTypes]         = useState<TestType[]>(savedData?.requiredTestTypes ?? DEFAULT.requiredTestTypes);
  const [coverage,          setCoverage]          = useState(savedData?.coverageThreshold ?? DEFAULT.coverageThreshold);
  const [manualCases,       setManualCases]       = useState(savedData?.requiresManualCases ?? DEFAULT.requiresManualCases);
  const [signOffRole,       setSignOffRole]       = useState<QAPolicyData['signOffRole']>(savedData?.signOffRole ?? DEFAULT.signOffRole);
  const [qaEnvironment,     setQaEnvironment]     = useState<QAPolicyData['qaEnvironment']>(savedData?.qaEnvironment ?? DEFAULT.qaEnvironment);
  const [testDataStrategy,  setTestDataStrategy]  = useState<QAPolicyData['testDataStrategy']>(savedData?.testDataStrategy ?? DEFAULT.testDataStrategy);
  const [blockDeploy,       setBlockDeploy]       = useState(savedData?.blockDeployOnFailure ?? DEFAULT.blockDeployOnFailure);

  const [saving,          setSaving]          = useState(false);
  const [saveError,       setSaveError]       = useState('');
  const [savedFlash,      setSavedFlash]      = useState(false);
  const [validationError, setValidationError] = useState('');

  // Load existing policy on mount. GET 404s when no row exists yet — that's
  // fine, the form keeps the schema defaults (matching qa_policies defaults).
  useEffect(() => {
    let cancelled = false;
    apiFetch<QAPolicyData>(`/tenants/${tenantId}/qa-policy`)
      .then(policy => {
        if (cancelled) return;
        setTestTypes(policy.requiredTestTypes as TestType[]);
        setCoverage(policy.coverageThreshold);
        setManualCases(policy.requiresManualCases);
        setSignOffRole(policy.signOffRole);
        setQaEnvironment(policy.qaEnvironment);
        setTestDataStrategy(policy.testDataStrategy);
        setBlockDeploy(policy.blockDeployOnFailure);
      })
      .catch(() => {}); // no policy yet — fine
    return () => { cancelled = true; };
  }, [tenantId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Read-only guard: the submit button is hidden when canEdit=false, but a
    // form submit can still be triggered programmatically. The authoritative
    // gate is the server's requireRole('TENANT_ADMIN') on POST; this just
    // makes the UI's read-only invariant hold at the JS layer too.
    if (!canEdit) return;
    setValidationError('');
    setSaveError('');
    setSavedFlash(false);

    if (testTypes.length === 0) {
      setValidationError('Please select at least one test type.');
      return;
    }
    if (coverage < 0 || coverage > 100) {
      setValidationError('Pass rate threshold must be between 0 and 100.');
      return;
    }

    setSaving(true);
    try {
      const body: QAPolicyData = {
        requiredTestTypes:    testTypes,
        coverageThreshold:    coverage,
        requiresManualCases:  manualCases,
        signOffRole,
        qaEnvironment,
        testDataStrategy,
        blockDeployOnFailure: blockDeploy,
      };

      await apiFetch(`/tenants/${tenantId}/qa-policy`, {
        method: 'POST',
        body:   JSON.stringify(body),
      });

      setSavedFlash(true);
      onSaved?.(body);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save QA policy');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={e => void handleSubmit(e)}>
      {showIntro && (
        <>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
            QA Policy
          </h3>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', marginBottom: 28 }}>
            Define your quality assurance standards, pass rate requirements, and release gates.
          </p>
        </>
      )}

      {/* Q1: Required test types */}
      <div style={fieldGap}>
        <label style={labelStyle}>
          Which test types are required for your organisation?{' '}
          <span style={{ color: 'var(--error)' }}>*</span>
        </label>
        <CheckboxGroup values={testTypes} onChange={v => setTestTypes(v)} disabled={!canEdit} />
        <p style={helpStyle}>
          BluePrint will generate test plans and CI gates for each selected type. The QA Agent
          will only require the types you select.
        </p>
      </div>

      {/* Q2: Pass rate threshold.
          "Pass rate threshold (%)": labelled this way per QA.A3 #950 + #948 — the
          underlying coverageThreshold field IS the pass-rate gate
          (qa-sessions.routes.ts:109-113); the original "Coverage threshold" label
          was semantically misleading. */}
      <div style={fieldGap}>
        <label style={labelStyle}>
          Pass rate threshold (%){' '}
          <span style={{ color: 'var(--error)' }}>*</span>
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <input
            type="number"
            min={0}
            max={100}
            step={1}
            value={coverage}
            disabled={!canEdit}
            onChange={e => setCoverage(Number(e.target.value))}
            style={{
              width: 80,
              padding: '10px 12px',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
              background: 'var(--surface-alt)',
              color: 'var(--text)',
              fontFamily: 'var(--font-body)',
              fontSize: 14,
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
          <span style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)' }}>%</span>
          <PassRateBadge value={coverage} />
        </div>
        <p style={helpStyle}>
          Sessions below this percentage are marked FAIL. Industry standard is 70–80% for most
          projects.
        </p>
      </div>

      {/* Q3: Manual test cases */}
      <div style={fieldGap}>
        <label style={labelStyle}>Are manual test cases required alongside automated tests?</label>
        <Toggle
          checked={manualCases}
          onChange={setManualCases}
          disabled={!canEdit}
          label="Manual test cases required"
          description="Requires manual test case documentation alongside automated suites"
        />
        <p style={helpStyle}>
          When enabled, the QA Agent will generate a manual test case template as part of Phase 6
          deliverables.
        </p>
      </div>

      {/* Q4: Sign-off role */}
      <div style={fieldGap}>
        <label style={labelStyle}>
          Who signs off on QA completion? <span style={{ color: 'var(--error)' }}>*</span>
        </label>
        <RadioGroup
          name="signOffRole"
          value={signOffRole}
          onChange={setSignOffRole}
          disabled={!canEdit}
          options={[
            { value: 'QA_ENGINEER',     label: 'QA Engineer',     description: 'The assigned QA Engineer approves the test results' },
            { value: 'TECH_LEAD',        label: 'Tech Lead',        description: 'The Tech Lead reviews and approves' },
            { value: 'PRODUCT_MANAGER',  label: 'Product Manager',  description: 'The PM signs off on acceptance criteria coverage' },
            { value: 'AUTOMATED_GATE',   label: 'Automated gate',   description: 'CI pipeline passes = QA approved, no human sign-off' },
          ]}
        />
        <p style={helpStyle}>
          BluePrint assigns the exit gate sign-off action item to this role at the end of Phase 6.
        </p>
      </div>

      {/* Q5: QA Environment */}
      <div style={fieldGap}>
        <label style={labelStyle}>
          What QA environment will you use? <span style={{ color: 'var(--error)' }}>*</span>
        </label>
        <RadioGroup
          name="qaEnvironment"
          value={qaEnvironment}
          onChange={setQaEnvironment}
          disabled={!canEdit}
          options={[
            { value: 'SHARED_STAGING', label: 'Shared staging',     description: 'All teams share a common staging environment' },
            { value: 'DEDICATED_QA',   label: 'Dedicated QA env',   description: 'Isolated environment reserved for QA testing' },
            { value: 'LOCAL_ONLY',     label: 'Local only',         description: 'Tests run locally on developer machines only' },
          ]}
        />
      </div>

      {/* Q6: Test data strategy */}
      <div style={fieldGap}>
        <label style={labelStyle}>
          What is your test data strategy? <span style={{ color: 'var(--error)' }}>*</span>
        </label>
        <RadioGroup
          name="testDataStrategy"
          value={testDataStrategy}
          onChange={setTestDataStrategy}
          disabled={!canEdit}
          options={[
            { value: 'FIXTURES',       label: 'Fixtures',                    description: 'Static test data files committed to the repository' },
            { value: 'GENERATED',      label: 'Generated',                   description: 'Test data generated programmatically per test run' },
            { value: 'ANONYMISED_PROD',label: 'Anonymised production data',  description: 'Sanitised copies of real production data' },
            { value: 'NONE',           label: 'None',                        description: 'Tests use live data or no data strategy defined' },
          ]}
        />
        <p style={helpStyle}>
          The QA Agent uses this to recommend appropriate test data setup patterns in Phase 6.
        </p>
      </div>

      {/* Q7: Block deploy on failure */}
      <div style={fieldGap}>
        <label style={labelStyle}>Should a failing QA gate hard-block deployment?</label>
        <Toggle
          checked={blockDeploy}
          onChange={setBlockDeploy}
          disabled={!canEdit}
          label="Block deployment on QA failure"
          description="Deployment cannot proceed until QA gates pass"
        />
        {!blockDeploy && (
          <div style={{
            marginTop: 8, padding: '10px 14px',
            background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)',
            borderRadius: 'var(--radius-sm)',
            fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--warning)',
          }}>
            ⚠ Disabling this allows deployment despite failing QA gates. Only recommended for
            non-production environments.
          </div>
        )}
        <p style={helpStyle}>
          When enabled, deployment cannot proceed until QA gates pass. When disabled, failures
          generate warnings only.
        </p>
      </div>

      {/* Validation error */}
      {validationError && (
        <div style={{
          marginBottom: 16, padding: '10px 12px',
          background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)',
          borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font-body)', fontSize: 13,
          color: 'var(--error)',
        }}>
          {validationError}
        </div>
      )}

      {/* Save error */}
      {saveError && (
        <div style={{
          marginBottom: 16, padding: '10px 12px',
          background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)',
          borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font-body)', fontSize: 13,
          color: 'var(--error)',
        }}>
          {saveError}
        </div>
      )}

      {/* Saved flash */}
      {savedFlash && !saveError && (
        <div style={{
          marginBottom: 16, padding: '10px 12px',
          background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.3)',
          borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font-body)', fontSize: 13,
          color: '#10b981',
        }}>
          QA policy saved.
        </div>
      )}

      {/* Submit — hidden in read-only mode */}
      {canEdit ? (
        <button
          type="submit"
          disabled={saving}
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 14,
            fontWeight: 600,
            padding: '10px 24px',
            background: 'var(--primary)',
            color: '#fff',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.6 : 1,
          }}
        >
          {saving ? 'Saving…' : submitLabel}
        </button>
      ) : (
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>
          Only tenant admins can edit the QA policy.
        </p>
      )}
    </form>
  );
}
